import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion, animate, useReducedMotion } from 'framer-motion';
import {
  Users,
  Calendar,
  Dumbbell,
  MessageSquare,
  TrendingUp,
  ArrowRight,
  Clock,
  Star,
} from 'lucide-react';
import { studentsAPI, plansAPI, exercisesAPI, commentsAPI } from '../lib/api';
import { StatCardSkeleton } from '../components/ui/LoadingSpinner';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

/** Conteo animado para los números hero (respeta reduced-motion). */
function CountUp({ value }: { value: number }) {
  const reduce = useReducedMotion();
  const [display, setDisplay] = useState(reduce ? value : 0);

  useEffect(() => {
    if (reduce) {
      setDisplay(value);
      return;
    }
    const controls = animate(0, value, {
      duration: 0.9,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [value, reduce]);

  return <>{display}</>;
}

export function Dashboard() {
  const { data: students, isLoading: loadingStudents } = useQuery({
    queryKey: ['students'],
    queryFn: () => studentsAPI.getAll({ active: true }),
  });

  const { data: plans, isLoading: loadingPlans } = useQuery({
    queryKey: ['plans'],
    queryFn: () => plansAPI.getAll({ active: true }),
  });

  const { data: exercises, isLoading: loadingExercises } = useQuery({
    queryKey: ['exercises'],
    queryFn: () => exercisesAPI.getAll(),
  });

  const { data: recentComments, isLoading: loadingComments } = useQuery({
    queryKey: ['comments', 'recent'],
    queryFn: () => commentsAPI.getRecent(),
  });

  const isLoading = loadingStudents || loadingPlans || loadingExercises || loadingComments;

  const stats = [
    {
      label: 'Alumnos Activos',
      value: students?.length || 0,
      icon: Users,
      color: 'primary',
      href: '/students',
    },
    {
      label: 'Planes Activos',
      value: plans?.length || 0,
      icon: Calendar,
      color: 'accent',
      href: '/students',
    },
    {
      label: 'Ejercicios',
      value: exercises?.length || 0,
      icon: Dumbbell,
      color: 'purple',
      href: '/exercises',
    },
    {
      label: 'Comentarios',
      value: recentComments?.length || 0,
      icon: MessageSquare,
      color: 'blue',
      href: '/comments',
    },
  ];

  // Monocromo: los íconos van en un chip neutro; el dorado aparece solo en el
  // hover de la card (borde) como detalle. Diferenciación por label + ícono.
  const colorClasses = {
    primary: 'from-dark-700 to-dark-800 shadow-black/30',
    accent: 'from-dark-700 to-dark-800 shadow-black/30',
    purple: 'from-dark-700 to-dark-800 shadow-black/30',
    blue: 'from-dark-700 to-dark-800 shadow-black/30',
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="space-y-3">
          <div className="skeleton h-4 w-32" />
          <div className="skeleton h-12 w-64" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <StatCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-500/80">
          Panel de control
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-gradient mt-1">
          Dashboard
        </h1>
        <p className="section-subtitle">
          Bienvenido al panel de gestión de entrenamientos
        </p>
      </motion.div>

      {/* Stats Grid */}
      <motion.div
        variants={itemVariants}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
      >
        {stats.map((stat) => (
          <Link
            key={stat.label}
            to={stat.href}
            className="card-hover group"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-dark-400 text-sm font-medium">{stat.label}</p>
                <p className="mt-2 text-4xl font-display font-bold text-dark-50 num tabular-nums">
                  <CountUp value={stat.value} />
                </p>
              </div>
              <div
                className={`w-12 h-12 rounded-xl bg-gradient-to-br ${
                  colorClasses[stat.color as keyof typeof colorClasses]
                } flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 ease-fluid`}
              >
                <stat.icon className="w-6 h-6 text-primary-400" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-1 text-sm text-dark-400 group-hover:text-primary-400 transition-colors">
              <span>Ver detalles</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </Link>
        ))}
      </motion.div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Students */}
        <motion.div variants={itemVariants} className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-dark-50 flex items-center gap-2">
              <Users className="w-5 h-5 text-dark-300" />
              Alumnos Recientes
            </h2>
            <Link to="/students" className="text-sm text-primary-400 hover:text-primary-300 transition-colors">
              Ver todos
            </Link>
          </div>
          <div className="space-y-3">
            {students?.slice(0, 5).map((student: any) => (
              <Link
                key={student.id}
                to={`/students/${student.id}`}
                className="flex items-center gap-4 p-3 rounded-xl bg-dark-800/50 hover:bg-dark-800 transition-colors group"
              >
                <div className="w-10 h-10 rounded-full bg-dark-700 ring-1 ring-primary-500/30 flex items-center justify-center text-dark-50 font-medium">
                  {student.firstName[0]}{student.lastName[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-dark-100 truncate group-hover:text-primary-400 transition-colors">
                    {student.firstName} {student.lastName}
                  </p>
                  <p className="text-sm text-dark-400 truncate">{student.email}</p>
                </div>
                <div className="flex items-center gap-1 text-xs text-dark-500">
                  <Calendar className="w-3 h-3" />
                  {student._count?.plans || 0} planes
                </div>
              </Link>
            ))}
            {(!students || students.length === 0) && (
              <p className="text-dark-400 text-center py-8">
                No hay alumnos registrados
              </p>
            )}
          </div>
        </motion.div>

        {/* Recent Comments */}
        <motion.div variants={itemVariants} className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-dark-50 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-dark-300" />
              Comentarios Recientes
            </h2>
            <Link to="/comments" className="text-sm text-primary-400 hover:text-primary-300 transition-colors">
              Ver todos
            </Link>
          </div>
          <div className="space-y-3">
            {recentComments?.slice(0, 5).map((comment: any) => (
              <div
                key={comment.id}
                className="p-3 rounded-xl bg-dark-800/50"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-dark-700 ring-1 ring-primary-500/30 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                    {comment.student?.firstName?.[0]}{comment.student?.lastName?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-dark-100 text-sm">
                        {comment.student?.firstName} {comment.student?.lastName}
                      </span>
                      {comment.rating && (
                        <div className="flex items-center gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-3 h-3 ${
                                i < comment.rating
                                  ? 'text-primary-400 fill-primary-400'
                                  : 'text-dark-600'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-dark-300 text-sm line-clamp-2">
                      {comment.content}
                    </p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-dark-500">
                      <Clock className="w-3 h-3" />
                      {new Date(comment.createdAt).toLocaleDateString('es-AR')}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {(!recentComments || recentComments.length === 0) && (
              <p className="text-dark-400 text-center py-8">
                No hay comentarios recientes
              </p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div variants={itemVariants} className="card">
        <h2 className="text-lg font-semibold text-dark-50 mb-6 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-dark-300" />
          Acciones Rápidas
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/students"
            className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-br from-dark-800/60 to-dark-900/40 border border-dark-700/60 hover:border-primary-500/40 transition-colors group"
          >
            <div className="w-12 h-12 rounded-xl bg-dark-800 border border-dark-700 flex items-center justify-center group-hover:border-primary-500/40 transition-colors">
              <Users className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="font-medium text-dark-100">Nuevo Alumno</p>
              <p className="text-sm text-dark-400">Agregar alumno al sistema</p>
            </div>
          </Link>

          <Link
            to="/exercises"
            className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-br from-dark-800/60 to-dark-900/40 border border-dark-700/60 hover:border-primary-500/40 transition-colors group"
          >
            <div className="w-12 h-12 rounded-xl bg-dark-800 border border-dark-700 flex items-center justify-center group-hover:border-primary-500/40 transition-colors">
              <Dumbbell className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="font-medium text-dark-100">Biblioteca</p>
              <p className="text-sm text-dark-400">Gestionar ejercicios</p>
            </div>
          </Link>

          <Link
            to="/comments"
            className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-br from-dark-800/60 to-dark-900/40 border border-dark-700/60 hover:border-primary-500/40 transition-colors group"
          >
            <div className="w-12 h-12 rounded-xl bg-dark-800 border border-dark-700 flex items-center justify-center group-hover:border-primary-500/40 transition-colors">
              <MessageSquare className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="font-medium text-dark-100">Feedback</p>
              <p className="text-sm text-dark-400">Ver comentarios</p>
            </div>
          </Link>
        </div>
      </motion.div>
    </motion.div>
  );
}

